import cx from 'classnames';
import { useContext } from 'react';
import urlResolver from 'edrolo/utils/URLResolver';
import { useAttachments } from 'edrolo/domains/attachments/hooks';
import { Attachment, Exam } from '../../../../../../types/Assemblage';
import {
  Heading,
  utils as u,
  Pill,
  enums as e,
  Icon,
  CopyLink,
  Button,
  Loader,
} from 'edrolo-ui';
import { AttachmentParent } from '../../../../../../enums/Attachments';
import { Link } from 'react-router-dom';
import { ClassContext } from 'edrolo/context';
import s from './styles.scss';
import { CardAttachments } from '../CardAttachments';
import { SkillCoveragePopover } from '../SkillCoveragePopover';
import { ResponsesProgress, StudentProgress } from './components';
import { FeatureGate } from 'edrolo/domains/coursePortal/components/FeatureGate';
import { COURSE_FEATURES } from 'core/constants';
import { WaffleFlag } from 'edrolo/utils/Waffle';
import { NSW_COURSES_WITH_NEWLY_ADDED_QUESTION_SETS } from '../../utils/utils';
import { useCreateAndNavigateToTask } from 'edrolo/domains/coursePortal/hooks';
import { useAnalytics } from 'edrolo/analytics';
import { ANALYTICS_ORIGIN } from '../../constants';

interface Props {
  questionSet: Exam;
  topicId: TopicId;
  lessonId?: LessonId;
}

export const QuestionSetCard = ({ questionSet, topicId, lessonId }: Props) => {
  const {
    data: { assemblage, classId, isTeacherView },
  } = useContext(ClassContext);

  const { data, isLoading, error } = useAttachments(
    classId,
    AttachmentParent.EXAM,
    questionSet.id,
    isTeacherView
  );

  const { createAndNavigateToTask, isLoading: taskLoading } =
    useCreateAndNavigateToTask();

  const viewResponsesUrl = urlResolver.reverse('question-set-grid', {
    classId,
    examId: questionSet.id,
  });

  const url = urlResolver.reverse('question-set-index', {
    examId: questionSet.id,
    classId,
  });

  const attachments: Attachment[] =
    (!isLoading && !error && Object.values(data.toJS())) || [];

  const shortLinkProps = {
    id: questionSet.id,
    appLabel: 'exams',
    model: 'exam',
    queryString: '',
  };

  const { track } = useAnalytics({});

  // If the exam is submittable, it is an Exam Master exam.
  // Don't attempt to render it as a question set.
  if (questionSet.is_submittable) {
    return null;
  }

  return (
    <div
      className={cx(s.questionSetCard, {
        [s.highlight]: !questionSet.is_published,
      })}
      data-appcues="question-set-card"
    >
      <header>
        <div className={s.titleBlock}>
          <Link to={url}>
            <Pill variant={e.PillVariant.FADE} color={e.ColorHue.GREEN}>
              <Icon
                name={e.Icons.QUESTION}
                color={e.ColorHue.GREEN}
                className={u.mr1}
              />
              Questions
            </Pill>
          </Link>

          <Link to={url}>
            <Heading level={4} className={u.bold}>
              {questionSet.title}
            </Heading>
          </Link>

          <Pill
            variant={e.PillVariant.OUTLINE}
            color={e.ColorTheme.FOREGROUND}
            className={u.uppercase}
          >
            {questionSet.question_count} Questions
          </Pill>

          {/* TODO remove after June 2024 */}
          {/* Intentionally set to match the flag for the launch modal on the courses page.
              Both of these elements are considered part of question set rollout for NSW, and
              should be enabled/disabled together */}
          <WaffleFlag name="launch_modal">
            {Object.values(NSW_COURSES_WITH_NEWLY_ADDED_QUESTION_SETS).includes(
              assemblage.course.id
            ) && <Pill color={e.ColorHue.YELLOW}>New</Pill>}
          </WaffleFlag>

          {isTeacherView && questionSet.skills.length > 0 && (
            <SkillCoveragePopover skills={questionSet.skills} />
          )}

          {!questionSet.is_published && (
            <Pill color={e.ColorHue.GREY}>Unpublished</Pill>
          )}
        </div>

        <div className={cx(u.flex, u.gap2)}>
          {isTeacherView && (
            <>
              <Button.Link
                size={e.Size.SMALL}
                variant={e.Variant.GHOST}
                to={viewResponsesUrl}
              >
                <ResponsesProgress questionSet={questionSet} />
                View responses
              </Button.Link>

              <FeatureGate
                feature={COURSE_FEATURES.TASK_SETTING}
                courseId={assemblage.course.id}
              >
                <Button
                  variant={e.Variant.OUTLINE}
                  size={e.Size.SMALL}
                  onClick={() => {
                    createAndNavigateToTask({
                      courseId: assemblage.course.id,
                      topicId,
                      lessonId,
                      examId: questionSet.id,
                      classId,
                    });
                    track('Create Task - Started', {
                      origin: ANALYTICS_ORIGIN.QUESTIONS_CARD,
                    });
                  }}
                >
                  {taskLoading ? (
                    <Loader.Spinner className={u.mr2} />
                  ) : (
                    <Icon name={e.Icons.TASK} className={u.mr2} />
                  )}
                  Create task
                </Button>
              </FeatureGate>

              <CopyLink
                buttonProps={{
                  size: e.Size.SMALL,
                  variant: e.Variant.OUTLINE,
                }}
                shortLinkProps={shortLinkProps}
              />
            </>
          )}

          {!isTeacherView && (
            <StudentProgress url={url} questionSet={questionSet} />
          )}
        </div>
      </header>
      {attachments.length > 0 && (
        <footer>
          <CardAttachments attachments={attachments} />
        </footer>
      )}
    </div>
  );
};
